import DataRoomAppBar from "@/components/data-room/DataRoomAppBar";
import {
  ALL_ENTRIES_JOINED,
  CORPORATE_ENTRIES,
  DATA_ROOM_STATIC_ID,
  FINANCE_ENTRIES,
  findEntryByStaticId,
  HUMAN_RESOURCES_ENTRIES,
  INTELLECTUAL_PROPERTY_ENTRIES,
  OPERATIONS_ENTRIES,
  OTHER_DOCUMENTS_ENTRIES,
  ROOT_CATEGORIES,
  STOCKHOLDER_INFORMATION_ENTRIES,
} from "@/components/data-room/DataRoomCategoriesDefines";
import DataRoomFileDragDrop from "@/components/data-room/DataRoomFileDragDrop";
import DataRoomFileListCard from "@/components/data-room/DataRoomFileListCard";
import DataRoomFileUploadModal from "@/components/data-room/DataRoomFileUploadModal";
import useFileUploadProgress from "@/components/data-room/useFileUploadProgress";
import PageHead from "@/components/seo/PageHead";
import CommonButton from "@/components/utils/CommonButton";
import LazoModal from "@/components/utils/LazoModal";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { GeneralFileNodeType } from "@/enums/DocumentCenterFileType";
import { LazoPaths } from "@/enums/LazoPaths";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import DataRoomService from "@/services/DataRoomService";
import { customMuiTheme } from "@/utils/AppThemeStyle";
import { Box, styled, SxProps, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import AtomSVG from "../../assets/images/data-room/atom.svg";
import BuildingSVG from "../../assets/images/data-room/building.svg";
import DollarCircleSVG from "../../assets/images/data-room/currency-circle-dollar.svg";
import SimpleUserFolderSVG from "../../assets/images/data-room/folder-simple-user.svg";
import GearSVG from "../../assets/images/data-room/gear.svg";
import ShapesSVG from "../../assets/images/data-room/shapes.svg";
import FourUsersSVG from "../../assets/images/data-room/users-four.svg";
import { DataRoomFileSection } from "./OneDataRoomFilesPage";

export type DataRoomEntry = {
  staticId: DATA_ROOM_STATIC_ID;
  name: string;
  backendName: string;
  type: GeneralFileNodeType;
  entries?: DataRoomEntry[];
  count?: number;
  fileExternalId?: string;
  hidden?: boolean;
};

export type DataRoomFileCategory = {
  id: string;
  key: string;
  label: string;
  icon: JSX.Element;
  entry?: DataRoomEntry;
};

export default function OneDataRoomPage() {
  const { t } = useTranslation("virtualDataRooms");
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get("documentId");

  const { currentTenantMembership } = useTenantMemberships();

  const FILE_CATEGORIES: { [k: string]: DataRoomFileCategory } = {
    [ROOT_CATEGORIES.CORPORATE]: {
      id: "corporate",
      key: "corporate",
      label: t("oneDataRoomPage.categories.corporate"),
      icon: <img src={BuildingSVG.src} alt="corporate-svg" />,
      entry: CORPORATE_ENTRIES,
    },
    [ROOT_CATEGORIES.HUMAN_RESOURCES]: {
      id: "human-resources",
      key: "human-resources",
      label: t("oneDataRoomPage.categories.humanResources"),
      icon: <img src={FourUsersSVG.src} alt="human-resources-svg" />,
      entry: HUMAN_RESOURCES_ENTRIES,
    },
    [ROOT_CATEGORIES.STOCKHOLDER_INFORMATION]: {
      id: "stockholder-information",
      key: "stockholder-information",
      label: t("oneDataRoomPage.categories.stockholderInformation"),
      icon: <img src={SimpleUserFolderSVG.src} alt="stockholder-information-svg" />,
      entry: STOCKHOLDER_INFORMATION_ENTRIES,
    },
    [ROOT_CATEGORIES.OPERATIONS]: {
      id: "operations",
      key: "operations",
      label: t("oneDataRoomPage.categories.operations"),
      icon: <img src={GearSVG.src} alt="operations-svg" />,
      entry: OPERATIONS_ENTRIES,
    },
    [ROOT_CATEGORIES.INTELLECTUAL_PROPERTY]: {
      id: "intellectual-property",
      key: "intellectual-property",
      label: t("oneDataRoomPage.categories.intellectualProperty"),
      icon: <img src={AtomSVG.src} alt="intellectual-property-svg" />,
      entry: INTELLECTUAL_PROPERTY_ENTRIES,
    },
    [ROOT_CATEGORIES.FINANCE]: {
      id: "finance",
      key: "finance",
      label: t("oneDataRoomPage.categories.finance"),
      icon: <img src={DollarCircleSVG.src} alt="finance-svg" />,
      entry: FINANCE_ENTRIES,
    },
    [ROOT_CATEGORIES.OTHER_DOCUMENTS]: {
      id: "other-documents",
      key: "other-documents",
      label: t("oneDataRoomPage.categories.otherDocuments"),
      icon: <img src={ShapesSVG.src} alt="other-documents-svg" />,
      entry: OTHER_DOCUMENTS_ENTRIES,
    },
  };

  useEffect(() => {
    if (!documentId || !currentTenantMembership.tenant.externalId) return;
    DataRoomService.getByDocumentId(Number(documentId), currentTenantMembership.tenant.externalId).then((r) => {
      r.onSuccess((f) => {
        onSearchOptionClick(f);
      });
    });
  }, [documentId, currentTenantMembership.tenant.externalId]);

  const [openUploadConfirmModal, setOpenUploadConfirmModal] = useState<boolean>(false);
  const [openFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);

  const [categories, setCategories] = useState<{
    [k: string]: DataRoomFileCategory;
  }>({ ...FILE_CATEGORIES });

  // Load stats
  useEffect(() => {
    DataRoomService.getStats(currentTenantMembership.tenant.externalId, ALL_ENTRIES_JOINED).then((r) => {
      r.onSuccess((s) => {
        let clone = { ...categories };
        s.entries.forEach((e) => {
          Object.keys(clone).forEach((c) => {
            if (e.staticId === clone[c].entry?.staticId) {
              clone[c].entry = e;
            }
          });
        });
        setCategories(clone);
      });
      r.onError(() => {});
    });
  }, [currentTenantMembership.tenant.externalId]);

  const { files, setFiles, fileProgressMap, uploadedFiles, resetState, resetFileProgressMap } = useFileUploadProgress();

  function handleUploadConfirModalClose() {
    resetState();
    setOpenUploadConfirmModal(false);
  }

  function handleUploadConfirm() {
    resetFileProgressMap();
    DataRoomService.uploadFiles(files, currentTenantMembership.tenant.externalId).then((r) => {
      r.onError((e) => {
        NotificationService.error(t("oneDataRoomPage.errors.uploadError"));
        console.log("error: " + e);
      });
    });
    setOpenUploadConfirmModal(false);
    setOpenFileUploadModal(true);
  }

  function handleMainFileDragDropUploadFiles(files: File[]) {
    setFiles(files);
    setOpenUploadConfirmModal(true);
  }

  const navigate = useNavigate();
  function onSearchOptionClick(opt: DataRoomEntry | DataRoomFile) {
    let entry = null;
    if ("parentStaticId" in opt) {
      entry = findEntryByStaticId(opt.parentStaticId);
    } else {
      entry = opt;
    }
    navigate(LazoPaths.ONE_DATA_ROOM_FILES, {
      state: {
        section: DataRoomFileSection.MANAGE,
        entry: entry,
      },
    });
  }

  return (
    <>
      <PageHead namespace="virtualDataRooms" titleKey="oneDataRoomPage.meta.title" descriptionKey="oneDataRoomPage.meta.description" />
      <RootContainer>
        <DataRoomAppBar onClickMenuOpt={onSearchOptionClick} />
        <BodyContainer>
          <DataRoomFileDragDrop onFileUpload={handleMainFileDragDropUploadFiles} />
          <FileCategoriesContainer>
            {Object.keys(categories).map((k, index) => (
              <DataRoomFileListCard key={index} filesCount={categories[k].entry?.count} category={categories[k]} />
            ))}
          </FileCategoriesContainer>
        </BodyContainer>
      </RootContainer>
      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openUploadConfirmModal}
        title={t("oneDataRoomPage.uploadModal.title")}
        titleSxProps={{
          fontWeight: 600,
          fontFamily: customMuiTheme.typography.fontFamily,
        }}
        handleClose={handleUploadConfirModalClose}
        buttons={[
          <CommonButton
            key="cancel-file-uploading-button"
            style={{
              borderRadius: "0.5rem",
              fontWeight: customMuiTheme.typography.fontWeightBold,
            }}
            purpleBorderVariant
            onClick={handleUploadConfirModalClose}
          >
            {t("oneDataRoomPage.uploadModal.buttons.cancel")}
          </CommonButton>,
          <CommonButton
            key="upload-file-button"
            style={{
              borderRadius: "0.5rem",
              fontWeight: customMuiTheme.typography.fontWeightBold,
            }}
            onClick={handleUploadConfirm}
          >
            {t("oneDataRoomPage.uploadModal.buttons.upload")}
          </CommonButton>,
        ]}
      >
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
          {t("oneDataRoomPage.uploadModal.message", { count: files.length })}
        </Typography>
      </LazoModal>
      <DataRoomFileUploadModal
        files={files}
        setFiles={setFiles}
        uploadedFiles={uploadedFiles}
        fileProgressMap={fileProgressMap}
        resetFileProgressMap={resetFileProgressMap}
        open={openFileUploadModal}
        setOpen={setOpenFileUploadModal}
        uploadFunc={handleUploadConfirm}
      />
    </>
  );
}

const RootContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  minHeight: "100%",
  height: "fit-content",
  scrollbarWidth: "none",
  paddingBottom: "2rem",
});

const BodyContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: "1rem",
  padding: "1rem 1.5rem 1.5rem 1.5rem",
});

const FileCategoriesContainer = styled(Box)({
  display: "grid",
  gap: "1rem",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  alignItems: "start",
});

export const DataRoomLazoModalSx: SxProps = {
  "& .title-container": {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
  },
  "&.modal-container": {
    maxWidth: "23.8125rem",
  },
};
